import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import type {
  CHANGE_SET_EXTRACTOR_TYPE,
  NameConverter,
  OnRevert,
  PivotClassifier,
  PivotValueCalculator,
  QueryToFormConverter,
  ValueConverter,
} from '@/_containers';
import { historyItemStringConverter, newValueCalculator } from '@/_containers';
import type {
  InventoryFormSchema,
  InventoryQuerySchema,
  InventoryUpdateSchema,
} from '@/_pages/settings/inventory-kits/shared/schemas/inventory';
import type { OnUpdate } from '@/_schemas/types';
import { resetFormState } from '@/_shared';

import { invalidateInventoryQuery } from '../apis';
import { fieldNames, inventoryFormToUpdate, inventoryQueryToForm } from '../converters';

export function useRevisionHistory(
  form: UseFormReturn<InventoryFormSchema>,
  onUpdate: OnUpdate<InventoryUpdateSchema, { data: InventoryQuerySchema }>
) {
  const validatePivot = useCallback((field: keyof InventoryFormSchema) => {
    return field === 'images' || field === 'logo';
  }, []);

  const onRevert: OnRevert<InventoryFormSchema> = useCallback(
    async (oldValue, field) => {
      const allFormValues = form.getValues();
      let nV = {} as InventoryUpdateSchema;
      const isPivot = !!field && validatePivot(field);

      if (!isPivot) {
        nV = inventoryFormToUpdate({ ...allFormValues, ...oldValue });
      } else {
        nV = inventoryFormToUpdate({ ...allFormValues });
        // TODO: Fix type
        nV = { ...nV, [field]: oldValue as number[] };
      }

      const res = await onUpdate(nV, 'Inventory changes have been reverted.', true, false);
      if (res.data) {
        if (isPivot) {
          invalidateInventoryQuery(nV.id + '');
        } else {
          resetFormState(form, { ...allFormValues, ...oldValue });
        }
      }
    },
    [form, onUpdate, validatePivot]
  );

  const queryToFormConverter: QueryToFormConverter<InventoryQuerySchema, InventoryFormSchema> = useCallback((q) => {
    return inventoryQueryToForm({ ...q });
  }, []);

  const nameConverter: NameConverter<InventoryFormSchema> = useCallback((f) => {
    // if key does not exist in fieldNames or field is not revertible
    if (!(typeof fieldNames[f] !== 'undefined' && fieldNames[f] !== null) || !fieldNames[f].revertible) return null;
    return fieldNames[f].name;
  }, []);

  const valueConverter: ValueConverter<InventoryFormSchema> = useCallback((f, form) => {
    // if key does not exist in fieldNames or field is not revertible
    const v = form[f];
    if (
      typeof fieldNames[f] === 'undefined' ||
      fieldNames[f] === null ||
      !fieldNames[f].revertible ||
      v === null ||
      typeof v === 'undefined'
    )
      return null;

    if (typeof v === 'object') {
      if ('name' in v) {
        return v.name;
      }
      return f;
    } else if (typeof v === 'string') {
      return historyItemStringConverter(v);
    } else if (typeof v === 'number') {
      return v + '';
    }

    return null;
  }, []);

  const pivotClassifier: PivotClassifier<InventoryFormSchema> = useCallback(
    (f) => {
      const isPivot = validatePivot(f);
      let changeSetExtractorType: CHANGE_SET_EXTRACTOR_TYPE = null;
      if (isPivot) {
        switch (f) {
          case 'images':
            changeSetExtractorType = 'MEDIA';
            break;
        }
      }
      return { isPivot, changeSetExtractorType };
    },
    [validatePivot]
  );

  const pivotValueCalculator: PivotValueCalculator<InventoryFormSchema> = useCallback(
    (field, items) => {
      const allFormValues = form.getValues();
      if (validatePivot(field)) {
        const nV = inventoryFormToUpdate({ ...allFormValues });
        let calcValue = nV[field];

        items.forEach(([value, type]) => {
          if (value !== null && typeof value !== 'undefined') {
            const oldValueIds = value[field]?.map((item) => item.id) ?? [];
            calcValue = newValueCalculator(oldValueIds, calcValue, type);
          }
        });
        return calcValue;
      }
      return [];
    },
    [form, validatePivot]
  );

  return {
    queryToFormConverter,
    nameConverter,
    valueConverter,
    onRevert,
    validatePivot,
    pivotClassifier,
    pivotValueCalculator,
  };
}
